import { useState, useCallback } from "react";
import { toast } from "sonner";

// ============= R2 CONFIGURATION =============
const R2_CONFIG = {
  BUCKET_NAME: import.meta.env.VITE_BUCKET_NAME,
  ACCESS_KEY: import.meta.env.VITE_R2_ACCESS_KEY,
  SECRET_KEY: import.meta.env.VITE_R2_SECRET_KEY,
  ENDPOINT: import.meta.env.VITE_R2_ENDPOINT_URL,
  PUBLIC_URL: import.meta.env.VITE_R2_PUBLIC_URL,
};

// ============= CRYPTO HELPERS =============
const bufferToHex = (buffer) => {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const hmacSHA256 = async (key, data) => {
  const encoder = new TextEncoder();
  const keyData = typeof key === "string" ? encoder.encode(key) : key;
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    cryptoKey,
    encoder.encode(data),
  );
  return new Uint8Array(signature);
};

const sha256Hex = async (data) => {
  const encoder = new TextEncoder();
  const dataToHash = typeof data === "string" ? encoder.encode(data) : data;
  const hash = await crypto.subtle.digest("SHA-256", dataToHash);
  return bufferToHex(hash);
};

const getSignatureKey = async (key, dateStamp, regionName, serviceName) => {
  const kDate = await hmacSHA256("AWS4" + key, dateStamp);
  const kRegion = await hmacSHA256(kDate, regionName);
  const kService = await hmacSHA256(kRegion, serviceName);
  return await hmacSHA256(kService, "aws4_request");
};

export const useR2Upload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const uploadFile = useCallback(async (file, path, options = {}) => {
    if (!file) return null;

    const {
      silent = false,
      successMessage = "File uploaded successfully!",
      errorMessage = "File upload failed. Please try again.",
      bucketName,
      publicUrlBase,
      cacheBust = true,
      cacheControl,
    } = options || {};

    setIsUploading(true);
    setUploadError(null);

    try {
      const resolvedBucketName = (bucketName || R2_CONFIG.BUCKET_NAME || "").trim();
      const resolvedPublicUrlBase = (publicUrlBase || R2_CONFIG.PUBLIC_URL || "").replace(/\/+$/g, "");

      if (!resolvedBucketName) {
        throw new Error("R2 bucket name is missing (check VITE_BUCKET_NAME)");
      }
      if (!resolvedPublicUrlBase) {
        throw new Error("R2 public URL is missing (check VITE_R2_PUBLIC_URL)");
      }

      const normalizedPath = path.replace(/^\/+/, "");
      const objectKey = `${normalizedPath}`;
      const fullPath = `/${resolvedBucketName}/${objectKey}`;

      const arrayBuffer = await file.arrayBuffer();
      const fileBytes = new Uint8Array(arrayBuffer);
      const payloadHash = await sha256Hex(fileBytes);

      const now = new Date();
      const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
      const dateStamp = amzDate.slice(0, 8);

      const host = new URL(R2_CONFIG.ENDPOINT).host;
      const region = "auto";
      const service = "s3";
      const algorithm = "AWS4-HMAC-SHA256";

      const canonicalUri = fullPath;
      const canonicalQuerystring = "";
      const canonicalHeaders = `host:${host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
      const signedHeaders = "host;x-amz-content-sha256;x-amz-date";

      const canonicalRequest = [
        "PUT",
        canonicalUri,
        canonicalQuerystring,
        canonicalHeaders,
        signedHeaders,
        payloadHash,
      ].join("\n");

      const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
      const canonicalRequestHash = await sha256Hex(canonicalRequest);
      const stringToSign = [
        algorithm,
        amzDate,
        credentialScope,
        canonicalRequestHash,
      ].join("\n");

      const signingKey = await getSignatureKey(
        R2_CONFIG.SECRET_KEY,
        dateStamp,
        region,
        service,
      );
      const signatureBytes = await crypto.subtle.sign(
        "HMAC",
        await crypto.subtle.importKey(
          "raw",
          signingKey,
          { name: "HMAC", hash: "SHA-256" },
          false,
          ["sign"],
        ),
        new TextEncoder().encode(stringToSign),
      );
      const signature = bufferToHex(signatureBytes);

      const authorizationHeader = `${algorithm} Credential=${R2_CONFIG.ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

      const url = `${R2_CONFIG.ENDPOINT}${fullPath}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Host: host,
          Authorization: authorizationHeader,
          "x-amz-date": amzDate,
          "x-amz-content-sha256": payloadHash,
          "Content-Type": file.type || "application/octet-stream",
          "Content-Length": fileBytes.length.toString(),
          ...(cacheControl ? { "Cache-Control": cacheControl } : {}),
        },
        body: fileBytes,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed (${response.status}): ${errorText}`);
      }

      const base = `${resolvedPublicUrlBase}/${objectKey}`;
      const publicUrl = cacheBust ? `${base}?t=${Date.now()}` : base;
      if (!silent) toast.success(successMessage);
      return publicUrl;
    } catch (error) {
      console.error("R2 Upload Error:", error);
      setUploadError(error.message);
      if (!silent) toast.error(errorMessage);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  return { isUploading, uploadError, uploadFile };
};
